import { z } from 'zod';

export const minimumPasswordLength = 8;
export const maximumBcryptPasswordBytes = 72;
export const emailTokenRequestCooldownSeconds = 60;

function utf8ByteLength(value: string) {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (codeUnit >= 0xD800 && codeUnit <= 0xDBFF && index + 1 < value.length) {
      const nextCodeUnit = value.charCodeAt(index + 1);
      if (nextCodeUnit >= 0xDC00 && nextCodeUnit <= 0xDFFF) {
        bytes += 4;
        index += 1;
      } else bytes += 3;
    } else bytes += 3;
  }
  return bytes;
}

export const newPasswordSchema = z.string()
  .max(maximumBcryptPasswordBytes, 'Şifre çok uzun.')
  .refine(
    value => Array.from(value).length >= minimumPasswordLength,
    `Şifre en az ${minimumPasswordLength} karakter olmalıdır.`,
  )
  .refine(
    value => utf8ByteLength(value) <= maximumBcryptPasswordBytes,
    `Şifre UTF-8 olarak en fazla ${maximumBcryptPasswordBytes} bayt olabilir.`,
  );

export const newPasswordConfirmationSchema = z.object({
  password: newPasswordSchema,
  passwordConfirmation: z.string(),
}).refine(
  value => value.password === value.passwordConfirmation,
  { message: 'Şifreler birbiriyle eşleşmiyor.', path: ['passwordConfirmation'] },
);

export const apiErrorSchema = z.object({
  message: z.string(),
  issues: z.array(z.unknown()).optional(),
});

export type ApiErrorPayload = z.infer<typeof apiErrorSchema>;

export const userRoleSchema = z.enum(['SYSTEM_ADMIN', 'COMPANY_USER', 'DRIVER']);

export type UserRole = z.infer<typeof userRoleSchema>;

export const companyPermissionSchema = z.enum([
  'QUOTE_VIEW',
  'QUOTE_CREATE',
  'QUOTE_EDIT',
  'MEMBER_VIEW',
  'MEMBER_MANAGE',
  'WALLET_VIEW',
]);

export type CompanyPermission = z.infer<typeof companyPermissionSchema>;

export const organizationRoleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isOwner: z.boolean(),
  permissions: z.array(companyPermissionSchema),
});

export type OrganizationRole = z.infer<typeof organizationRoleSchema>;

export const organizationSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string(),
  taxNumber: z.string().nullable().optional(),
  createdAt: z.string().optional(),
});

export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;

export const driverStatusSchema = z.enum(['PENDING', 'ACTIVE', 'SUSPENDED']);

export type DriverStatus = z.infer<typeof driverStatusSchema>;

export const assignmentStatusSchema = z.enum(['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']);

export type AssignmentStatus = z.infer<typeof assignmentStatusSchema>;

export const tripStatusSchema = z.enum(['PLANNED', 'ACTIVE', 'COMPLETED', 'CANCELLED']);

export type TripStatus = z.infer<typeof tripStatusSchema>;

export const quoteStatusSchema = z.enum(['DRAFT', 'SUBMITTED', 'REVIEWED', 'APPROVED', 'REJECTED']);

export type QuoteStatus = z.infer<typeof quoteStatusSchema>;

export const walletTransactionTypeSchema = z.enum(['CREDIT_TOPUP', 'ASSIGNMENT_DEBIT']);

export type WalletTransactionType = z.infer<typeof walletTransactionTypeSchema>;

export const auditOutcomeSchema = z.enum(['SUCCESS', 'FAILURE']);

export type AuditOutcome = z.infer<typeof auditOutcomeSchema>;

export const companyRegistrationSchema = z.object({
  companyName: z.string().min(2).max(120),
  address: z.string().min(5).max(500),
  taxNumber: z.string().regex(/^\d{10,11}$/).optional().nullable(),
  fullName: z.string().min(2).max(80),
  email: z.email(),
  password: newPasswordSchema,
});

export type CompanyRegistration = z.infer<typeof companyRegistrationSchema>;

export const driverVehicleInputSchema = z.object({
  vehicleTypeId: z.string().uuid(),
  plateNumber: z.string().trim().min(5).max(20),
  brand: z.string().trim().min(2).max(60),
  model: z.string().trim().min(1).max(60),
  modelYear: z.number().int().min(1950).max(2100).optional().nullable(),
  capacityTonnes: z.number().positive().max(1000).optional().nullable(),
});

export type DriverVehicleInput = z.infer<typeof driverVehicleInputSchema>;

export const driverVehicleUpdateSchema = driverVehicleInputSchema.extend({
  isActive: z.boolean(),
});

export type DriverVehicleUpdate = z.infer<typeof driverVehicleUpdateSchema>;

export const driverRegistrationSchema = z.object({
  fullName: z.string().trim().min(2).max(80),
  email: z.email(),
  password: newPasswordSchema,
  phone: z.string().trim().max(30).optional().nullable(),
  licenseNumber: z.string().trim().min(3).max(40).optional().nullable(),
  vehicle: driverVehicleInputSchema.optional().nullable(),
});

export type DriverRegistration = z.infer<typeof driverRegistrationSchema>;

export const registrationRequestResponseSchema = z.object({
  message: z.string(),
  retryAfterSeconds: z.number().int().min(1).max(3600),
  developmentVerificationToken: z.string().min(1).optional(),
});

export type RegistrationRequestResponse = z.infer<typeof registrationRequestResponseSchema>;

export const emailVerificationRequestSchema = z.object({
  token: z.string().regex(/^[a-f0-9]{64}$/i),
});

export type EmailVerificationRequest = z.infer<typeof emailVerificationRequestSchema>;

export const emailVerificationResponseSchema = z.object({
  message: z.string(),
});

export type EmailVerificationResponse = z.infer<typeof emailVerificationResponseSchema>;

export const registrationVerificationResendSchema = z.object({
  email: z.email(),
});

export type RegistrationVerificationResend = z.infer<typeof registrationVerificationResendSchema>;

export const passwordResetRequestResponseSchema = z.object({
  message: z.string(),
  retryAfterSeconds: z.number().int().min(1).max(3600),
  developmentResetToken: z.string().min(1).optional(),
});

export type PasswordResetRequestResponse = z.infer<typeof passwordResetRequestResponseSchema>;

export const pricingCatalogResponseSchema = z.object({
  configured: z.boolean(),
  currency: z.string().length(3),
  vehicleTypes: z.array(z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    available: z.boolean(),
  })),
});

export type PricingCatalogResponse = z.infer<typeof pricingCatalogResponseSchema>;

export const authUserSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  email: z.email(),
  role: userRoleSchema,
  phone: z.string().nullable().optional(),
  createdAt: z.string(),
  driverProfile: z.object({
    status: driverStatusSchema,
    licenseNumber: z.string().nullable().optional(),
  }).nullable().optional(),
  organization: organizationSummarySchema.extend({
    role: organizationRoleSchema,
  }).nullable().optional(),
});

export type AuthUser = z.infer<typeof authUserSchema>;

export const driverProfileUpdateSchema = z.object({
  fullName: z.string().trim().min(2).max(80),
  phone: z.string().trim().max(30).optional().nullable(),
  licenseNumber: z.string().trim().min(3).max(40).optional().nullable(),
});

export type DriverProfileUpdate = z.infer<typeof driverProfileUpdateSchema>;

export const loginResponseSchema = z.object({
  user: authUserSchema,
  token: z.string().min(1),
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const authMeResponseSchema = z.object({ user: authUserSchema });

export const accountDeletionRequestSchema = z.object({
  password: z.string().min(1).max(72),
  confirmation: z.literal('HESABIMI SIL'),
});

export type AccountDeletionRequest = z.infer<typeof accountDeletionRequestSchema>;

export const accountDeletionResponseSchema = z.object({
  message: z.string(),
  deletedAt: z.iso.datetime(),
});

export type AccountDeletionResponse = z.infer<typeof accountDeletionResponseSchema>;

export const clientTelemetryEventSchema = z.object({
  event: z.enum(['mobile_crash', 'web_crash', 'tracking_error']),
  platform: z.enum(['ios', 'android', 'web']),
  release: z.string().trim().min(1).max(40),
  errorName: z.string().trim().min(1).max(80),
  fingerprint: z.string().trim().min(1).max(160).regex(/^[a-zA-Z0-9._:-]+$/),
  context: z.enum(['startup', 'authentication', 'assignments', 'tracking', 'vehicles', 'profile', 'unknown']),
});

export type ClientTelemetryEvent = z.infer<typeof clientTelemetryEventSchema>;

export const driverVehicleSchema = z.object({
  id: z.string().uuid(),
  plateNumber: z.string(),
  brand: z.string(),
  model: z.string(),
  modelYear: z.number().int().nullable().optional(),
  capacityTonnes: z.number().nullable().optional(),
  isActive: z.boolean(),
  vehicleType: z.object({id: z.string().uuid(), name: z.string()}),
});

export type DriverVehicle = z.infer<typeof driverVehicleSchema>;

export const driverTripSchema = z.object({
  id: z.string().uuid(),
  status: tripStatusSchema,
  startedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  vehicle: driverVehicleSchema.nullable().optional(),
});

export type DriverTrip = z.infer<typeof driverTripSchema>;

export const driverAssignmentSchema = z.object({
  id: z.string().uuid(),
  status: assignmentStatusSchema,
  assignedAt: z.string(),
  updatedAt: z.string(),
  trip: driverTripSchema.nullable(),
  quote: z.object({
    id: z.string().uuid(),
    quoteNumber: z.string(),
    originLabel: z.string(),
    originLat: z.number(),
    originLon: z.number(),
    destinationLabel: z.string(),
    destinationLat: z.number(),
    destinationLon: z.number(),
    distanceKm: z.number(),
    durationMinutes: z.number().int(),
    loadingDate: z.string(),
    routeGeometry: z.unknown().nullable().optional(),
    totalPrice: z.number(),
    organization: z.object({ id: z.string().uuid(), name: z.string() }),
    vehicleType: z.object({ id: z.string().uuid(), name: z.string() }),
  }),
});

export type DriverAssignment = z.infer<typeof driverAssignmentSchema>;

export const driverAssignmentsResponseSchema = z.object({
  driverStatus: driverStatusSchema,
  assignments: z.array(driverAssignmentSchema),
});

export type DriverAssignmentsResponse = z.infer<typeof driverAssignmentsResponseSchema>;

export const driverAssignmentStatusUpdateSchema = z.object({
  status: z.enum(['ACCEPTED', 'IN_PROGRESS', 'COMPLETED']),
});

export type DriverAssignmentStatusUpdate = z.infer<typeof driverAssignmentStatusUpdateSchema>;

export const driverAssignmentVehicleSelectionSchema = z.object({
  vehicleId: z.string().uuid(),
});

export type DriverAssignmentVehicleSelection = z.infer<typeof driverAssignmentVehicleSelectionSchema>;

export const assignmentStatusUpdateResponseSchema = z.object({
  assignment: z.object({
    id: z.string().uuid(),
    status: assignmentStatusSchema,
    updatedAt: z.string(),
  }),
});

export type AssignmentStatusUpdateResponse = z.infer<typeof assignmentStatusUpdateResponseSchema>;

export const driverVehiclesResponseSchema = z.object({
  vehicles: z.array(driverVehicleSchema),
});

export type DriverVehiclesResponse = z.infer<typeof driverVehiclesResponseSchema>;

export const driverVehicleResponseSchema = z.object({
  vehicle: driverVehicleSchema,
});

export type DriverVehicleResponse = z.infer<typeof driverVehicleResponseSchema>;

export const driverTripResponseSchema = z.object({
  trip: driverTripSchema,
});

export type DriverTripResponse = z.infer<typeof driverTripResponseSchema>;

export const locationEventInputSchema = z.object({
  eventId: z.string().uuid(),
  sequenceNumber: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  recordedAt: z.iso.datetime(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy: z.number().min(0).max(5000),
  speed: z.number().min(0).max(150).optional().nullable(),
  heading: z.number().min(0).lt(360).optional().nullable(),
  altitude: z.number().min(-1000).max(20000).optional().nullable(),
});

export type LocationEventInput = z.infer<typeof locationEventInputSchema>;

export const storedLocationEventSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  tripId: z.string().uuid(),
  sequenceNumber: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  recordedAt: z.iso.datetime(),
  receivedAt: z.iso.datetime(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy: z.number().min(0).max(5000),
  speed: z.number().min(0).max(150).nullable(),
  heading: z.number().min(0).lt(360).nullable(),
  altitude: z.number().min(-1000).max(20000).nullable(),
});

export type StoredLocationEvent = z.infer<typeof storedLocationEventSchema>;

export const MAX_LOCATION_BATCH_SIZE = 100;

export const tripLocationReadyMessageSchema = z.object({
  type: z.literal('trip.location.ready'),
  tripId: z.string().uuid(),
  connectedAt: z.iso.datetime(),
});

export const tripLocationBatchMessageSchema = z.object({
  type: z.literal('trip.location.batch'),
  tripId: z.string().uuid(),
  locations: z.array(storedLocationEventSchema).min(1).max(MAX_LOCATION_BATCH_SIZE),
});

export const tripLocationRealtimeMessageSchema = z.discriminatedUnion('type', [
  tripLocationReadyMessageSchema,
  tripLocationBatchMessageSchema,
]);

export type TripLocationReadyMessage = z.infer<typeof tripLocationReadyMessageSchema>;
export type TripLocationBatchMessage = z.infer<typeof tripLocationBatchMessageSchema>;
export type TripLocationRealtimeMessage = z.infer<typeof tripLocationRealtimeMessageSchema>;

export const locationBatchSchema = z.object({
  events: z.array(locationEventInputSchema).min(1).max(MAX_LOCATION_BATCH_SIZE),
});

export type LocationBatch = z.infer<typeof locationBatchSchema>;

export const locationBatchResponseSchema = z.object({
  acceptedCount: z.number().int().nonnegative(),
  duplicateCount: z.number().int().nonnegative(),
  deferredCount: z.number().int().nonnegative().optional(),
});

export type LocationBatchResponse = z.infer<typeof locationBatchResponseSchema>;

export const organizationProfileUpdateSchema = z.object({
  companyName: z.string().trim().min(2).max(120),
  address: z.string().trim().min(5).max(500),
  taxNumber: z.string().regex(/^\d{10,11}$/).optional().nullable(),
});

export type OrganizationProfileUpdate = z.infer<typeof organizationProfileUpdateSchema>;

export const organizationMeResponseSchema = z.object({
  organization: organizationSummarySchema,
  role: organizationRoleSchema,
});

export type OrganizationMeResponse = z.infer<typeof organizationMeResponseSchema>;

export const organizationProfileResponseSchema = z.object({
  organization: organizationSummarySchema,
});

export type OrganizationProfileResponse = z.infer<typeof organizationProfileResponseSchema>;

export const organizationRoleWithCountSchema = organizationRoleSchema.extend({
  _count: z.object({memberships: z.number().int().nonnegative()}),
});

export type OrganizationRoleWithCount = z.infer<typeof organizationRoleWithCountSchema>;

export const organizationRolesResponseSchema = z.object({
  roles: z.array(organizationRoleWithCountSchema),
});

export type OrganizationRolesResponse = z.infer<typeof organizationRolesResponseSchema>;

export const organizationRoleCreateSchema = z.object({
  name: z.string().trim().min(2).max(60),
  permissions: z.array(companyPermissionSchema).min(1),
});

export type OrganizationRoleCreate = z.infer<typeof organizationRoleCreateSchema>;

export const organizationRoleResponseSchema = z.object({role: organizationRoleSchema});

export type OrganizationRoleResponse = z.infer<typeof organizationRoleResponseSchema>;

export const organizationMemberSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  user: z.object({
    id: z.string().uuid(),
    fullName: z.string(),
    email: z.email(),
    phone: z.string().nullable().optional(),
  }),
  role: organizationRoleSchema,
});

export type OrganizationMember = z.infer<typeof organizationMemberSchema>;

export const organizationMembersResponseSchema = z.object({
  members: z.array(organizationMemberSchema),
});

export type OrganizationMembersResponse = z.infer<typeof organizationMembersResponseSchema>;

export const organizationMemberCreateSchema = z.object({
  fullName: z.string().trim().min(2).max(80),
  email: z.email(),
  password: newPasswordSchema,
  phone: z.string().trim().max(30).optional().nullable(),
  roleId: z.string().uuid(),
});

export type OrganizationMemberCreate = z.infer<typeof organizationMemberCreateSchema>;

export const organizationMemberCreateResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    fullName: z.string(),
    email: z.email(),
    phone: z.string().nullable().optional(),
  }),
  roleId: z.string().uuid(),
});

export type OrganizationMemberCreateResponse = z.infer<typeof organizationMemberCreateResponseSchema>;

export const organizationMemberRoleUpdateSchema = z.object({roleId: z.string().uuid()});

export type OrganizationMemberRoleUpdate = z.infer<typeof organizationMemberRoleUpdateSchema>;

export const walletTransactionIdSchema = z.union([
  z.string().uuid(),
  z.string().regex(/^[0-9a-f]{32}$/),
]);

export const walletTransactionSchema = z.object({
  id: walletTransactionIdSchema,
  type: walletTransactionTypeSchema,
  amount: z.number(),
  balanceAfter: z.number(),
  description: z.string().nullable().optional(),
  createdAt: z.string(),
  quote: z.object({id: z.string().uuid(), quoteNumber: z.string()}).nullable().optional(),
  actor: z.object({fullName: z.string()}).nullable().optional(),
});

export type WalletTransaction = z.infer<typeof walletTransactionSchema>;

export const organizationWalletResponseSchema = z.object({
  wallet: z.object({
    balance: z.number(),
    currency: z.string().length(3),
    updatedAt: z.string(),
  }),
  transactions: z.array(walletTransactionSchema),
});

export type OrganizationWalletResponse = z.infer<typeof organizationWalletResponseSchema>;

export const geoPointSchema = z.object({
  label: z.string().min(2).max(300),
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
});

export type GeoPoint = z.infer<typeof geoPointSchema>;

export const geoPlaceSchema = geoPointSchema.extend({
  id: z.string(),
  type: z.string().optional(),
});

export type GeoPlace = z.infer<typeof geoPlaceSchema>;

export const geoSearchResponseSchema = z.array(geoPlaceSchema);

export type GeoSearchResponse = z.infer<typeof geoSearchResponseSchema>;

export const routeGeometrySchema = z.object({
  type: z.literal('LineString'),
  coordinates: z.array(z.tuple([z.number(), z.number()])).min(2),
});

export const geoRouteResponseSchema = z.object({
  distanceKm: z.number().positive(),
  durationMinutes: z.number().int().positive(),
  geometry: routeGeometrySchema,
});

export type GeoRouteResponse = z.infer<typeof geoRouteResponseSchema>;

export const priceCalculationRequestSchema = z.object({
  vehicleTypeId: z.string().uuid(),
  distanceKm: z.number().positive().max(10000),
  tonnage: z.number().positive().max(1000),
});

export type PriceCalculationRequest = z.infer<typeof priceCalculationRequestSchema>;

export const priceCalculationResponseSchema = z.object({
  totalPrice: z.number(),
  currency: z.string().length(3),
});

export type PriceCalculationResponse = z.infer<typeof priceCalculationResponseSchema>;

export const companyQuoteCreateSchema = z.object({
  origin: geoPointSchema,
  destination: geoPointSchema,
  vehicleTypeId: z.string().uuid(),
  tonnage: z.number().positive().max(1000),
  loadingDate: z.iso.date(),
  distanceKm: z.number().positive().max(10000),
  durationMinutes: z.number().int().positive(),
  routeGeometry: routeGeometrySchema,
});

export type CompanyQuoteCreate = z.infer<typeof companyQuoteCreateSchema>;

export const companyQuoteSchema = z.object({
  id: z.string().uuid(),
  quoteNumber: z.string(),
  status: quoteStatusSchema,
  originLabel: z.string(),
  originLat: z.number(),
  originLon: z.number(),
  destinationLabel: z.string(),
  destinationLat: z.number(),
  destinationLon: z.number(),
  distanceKm: z.number(),
  durationMinutes: z.number().int(),
  tonnage: z.number(),
  loadingDate: z.string(),
  totalPrice: z.number(),
  routeGeometry: routeGeometrySchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  vehicleType: z.object({id: z.string().uuid().optional(), name: z.string()}),
  assignment: z.object({
    id: z.string().uuid(),
    driverId: z.string().uuid(),
    status: assignmentStatusSchema,
    driver: z.object({fullName: z.string()}),
    trip: z.object({
      id: z.string().uuid(),
      status: tripStatusSchema,
      startedAt: z.string().nullable().optional(),
      completedAt: z.string().nullable().optional(),
    }).nullable().optional(),
  }).nullable().optional(),
});

export type CompanyQuote = z.infer<typeof companyQuoteSchema>;

export const companyQuotesResponseSchema = z.object({quotes: z.array(companyQuoteSchema)});
export const companyQuoteResponseSchema = z.object({quote: companyQuoteSchema});

export type CompanyQuotesResponse = z.infer<typeof companyQuotesResponseSchema>;
export type CompanyQuoteResponse = z.infer<typeof companyQuoteResponseSchema>;

export const companyTripLocationEventSchema = storedLocationEventSchema.omit({
  eventId: true,
  tripId: true,
});

export type CompanyTripLocationEvent = z.infer<typeof companyTripLocationEventSchema>;

export const companyTripLocationHistoryResponseSchema = z.object({
  trip: z.object({
    id: z.string().uuid(),
    status: tripStatusSchema,
    startedAt: z.string().nullable().optional(),
    completedAt: z.string().nullable().optional(),
    createdAt: z.string(),
    driver: z.object({
      id: z.string().uuid(),
      fullName: z.string(),
      phone: z.string().nullable().optional(),
    }),
    vehicle: driverVehicleSchema.extend({createdAt: z.string()}).nullable().optional(),
    assignment: z.object({
      id: z.string().uuid(),
      status: assignmentStatusSchema,
      quote: z.object({
        id: z.string().uuid(),
        quoteNumber: z.string(),
        originLabel: z.string(),
        originLat: z.number(),
        originLon: z.number(),
        destinationLabel: z.string(),
        destinationLat: z.number(),
        destinationLon: z.number(),
        organization: z.object({id: z.string().uuid(), name: z.string()}),
      }),
    }),
  }),
  locations: z.array(companyTripLocationEventSchema),
  pagination: z.object({
    limit: z.number().int().positive(),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
  latestLocation: companyTripLocationEventSchema.nullable(),
});

export type CompanyTripLocationHistoryResponse = z.infer<typeof companyTripLocationHistoryResponseSchema>;
